import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Tracks which top-level module routes the user has already visited in this
 * session. The scope switcher reads this set to decide which already-opened
 * screens' data to lazily prefetch in the background when the active scope
 * changes — so switching back to a previously-visited screen is instant
 * instead of flashing a skeleton.
 *
 * Lives outside the auth store so route tracking doesn't churn auth state, and
 * is intentionally NOT persisted: it's a per-session warm-cache hint, not
 * durable state.
 */
export const useVisitedRoutesStore = defineStore('visitedRoutes', () => {
  /** Route names the user has landed on (e.g. 'home', 'people', 'pay'). */
  const visited = ref<Set<string>>(new Set());

  /** Record a visit to a named route. No-op for anonymous/unnamed routes. */
  function markVisited(name: string | null | undefined): void {
    if (!name) return;
    if (visited.value.has(name)) return;
    // Reassign so dependent computeds/watchers re-run (Set mutation isn't
    // reactive on its own).
    const next = new Set(visited.value);
    next.add(name);
    visited.value = next;
  }

  /** Snapshot of visited route names. */
  function list(): string[] {
    return Array.from(visited.value);
  }

  function reset(): void {
    visited.value = new Set();
  }

  return { visited, markVisited, list, reset };
});
